/**
 * 客户端 CSPRNG 适配器（红线 #15）。
 *
 * shared 刻意不提供密码学随机源：它是平台中立包，import `node:crypto` 会让浏览器打包失败，
 * 而内置 `Math.random` 洗牌等于把「可预测的发牌」摆在手边等人误用。
 * 所以真实发牌的随机源由各端自己注入 —— 服务端注 `crypto.randomInt`，这里注
 * `crypto.getRandomValues`，两边都复用 shared 的 `rngFromUint32Source` 做**拒绝采样**。
 *
 * 为什么必须复用 `rngFromUint32Source` 而不是自己写 `raw % n`：
 * 2^32 不是 52! 的因数，直接取模会让牌堆前 `2^32 mod n` 个位置各多出现一次。
 * 单手牌看不出来，几万手之后就是系统性偏袒。
 *
 * 注意 `getRandomValues` 在**非安全上下文也可用**（不像 `crypto.subtle`），
 * 所以手机通过局域网 http://192.168.x.x 打开开发服务器时它照样工作。
 */
import { rngFromUint32Source, type Rng } from '@poker/shared'

/**
 * 一次抽 64 个 uint32 缓存起来。
 * `getRandomValues` 每次调用都有固定开销，而洗一副牌要 51 次 `nextInt`（拒绝采样偶尔重抽），
 * 逐个取会把一次洗牌变成 50+ 次跨边界调用。缓存里的每个字仍然是独立的 CSPRNG 输出。
 */
const BATCH_WORDS = 64

/** 环境没有 Web Crypto —— 绝不降级到 `Math.random`，宁可开不了局。 */
export class NoCryptoSourceError extends Error {
  constructor() {
    super('当前环境没有 crypto.getRandomValues，无法安全洗牌；本应用拒绝用 Math.random 发牌')
    this.name = 'NoCryptoSourceError'
  }
}

/** 环境是否具备安全洗牌的能力。开局前用它给出可读的报错，而不是等到洗牌时抛。 */
export function hasWebCryptoRng(source?: Crypto): boolean {
  const c = source ?? globalThis.crypto
  return c !== undefined && typeof c.getRandomValues === 'function'
}

/**
 * `crypto.getRandomValues` + 拒绝采样的 `Rng`。这是客户端唯一允许喂给 `shuffleDeck` 的随机源。
 *
 * @param source 仅供测试注入（可以塞一个确定性的 uint32 源来验证拒绝采样的行为）。
 */
export function createWebCryptoRng(source?: Crypto): Rng {
  const crypto = source ?? globalThis.crypto
  if (crypto === undefined || typeof crypto.getRandomValues !== 'function') {
    throw new NoCryptoSourceError()
  }
  const buf = new Uint32Array(BATCH_WORDS)
  // 初值 = 长度，保证第一次取数就先填充
  let cursor = buf.length

  return rngFromUint32Source(() => {
    if (cursor >= buf.length) {
      crypto.getRandomValues(buf)
      cursor = 0
    }
    // noUncheckedIndexedAccess：cursor 一定落在 [0, length) 内，但类型系统看不出来
    const v = buf[cursor] ?? 0
    cursor++
    return v >>> 0
  })
}
